// src/utils/errorHandler.js
// Consistent error handling utilities

import { captureException } from "../config/sentry";

/**
 * Display error message to user
 * @param {string|Error|Object} error - Error to handle
 * @param {string} context - Context where error occurred
 * @returns {string} - Formatted error message
 */
export const handleServiceError = (error, context = "") => {
  let message = "An unexpected error occurred";

  if (typeof error === "string") {
    message = error;
  } else if (error?.message) {
    message = error.message;
  } else if (error?.error) {
    message = error.error;
  }

  const fullMessage = context ? `${context}: ${message}` : message;

  // Log to console in development
  if (process.env.NODE_ENV === "development") {
    console.error(`❌ ${fullMessage}`, error);
  }

  // Send to Sentry error tracking
  const errorObj = error instanceof Error ? error : new Error(fullMessage);
  captureException(errorObj, {
    tags: { context },
    extra: { originalError: error, message: fullMessage },
  });

  return message;
};

/**
 * Check if result is successful
 * @param {Object} result - Service result object
 * @returns {boolean}
 */
export const isSuccess = (result) => {
  return result && result.success === true;
};

/**
 * Extract data from service result safely
 * @param {Object} result - Service result object
 * @param {*} fallback - Fallback value if unsuccessful
 * @returns {*} - Data or fallback
 */
export const getData = (result, fallback = null) => {
  return isSuccess(result) ? result.data : fallback;
};

/**
 * Extract error from service result
 * @param {Object} result - Service result object
 * @param {string} defaultError - Default error message
 * @returns {string|null}
 */
export const getError = (result, defaultError = "Operation failed") => {
  if (isSuccess(result)) return null;
  return result?.error || defaultError;
};

/**
 * Create standardized success response
 * @param {*} data - Success data
 * @returns {Object} - Standardized response
 */
export const createSuccessResponse = (data) => {
  return {
    success: true,
    data,
    error: null,
  };
};

/**
 * Create standardized error response
 * @param {string|Error} error - Error message or object
 * @param {*} data - Optional data to include
 * @returns {Object} - Standardized response
 */
export const createErrorResponse = (error, data = null) => {
  const errorMessage =
    typeof error === "string" ? error : error?.message || "Unknown error";

  return {
    success: false,
    data,
    error: errorMessage,
  };
};

/**
 * Wrap async function with error handling
 * @param {Function} fn - Async function to wrap
 * @param {string} context - Context for error messages
 * @returns {Function} - Wrapped function
 */
export const withErrorHandling = (fn, context) => {
  return async (...args) => {
    try {
      const result = await fn(...args);
      return result;
    } catch (error) {
      console.error(`Error in ${context}:`, error);
      return createErrorResponse(error);
    }
  };
};

/**
 * Logger utility (respects environment)
 */
export const logger = {
  debug: (...args) => {
    if (process.env.NODE_ENV === "development") {
      console.log("🔍", ...args);
    }
  },
  info: (...args) => {
    if (process.env.NODE_ENV === "development") {
      console.info("ℹ️", ...args);
    }
  },
  warn: (...args) => {
    console.warn("⚠️", ...args);
  },
  error: (...args) => {
    console.error("❌", ...args);
  },
  success: (...args) => {
    if (process.env.NODE_ENV === "development") {
      console.log("✅", ...args);
    }
  },
};

export default {
  handleServiceError,
  isSuccess,
  getData,
  getError,
  createSuccessResponse,
  createErrorResponse,
  withErrorHandling,
  logger,
};
